import { Card } from "@pericles/framework-frontend";

import {
  groupMilestonesByVersion,
  longwallsMilestones,
  longwallsVersionDefinitions,
  summarizeMilestones,
  type LongWallsMilestoneStatus,
  type LongWallsProductVersion,
  type LongWallsStrategicMilestone,
} from "../domain/longwallsMilestones";

const versionOrder: LongWallsProductVersion[] = ["foundation", "v1", "v2", "v3"];

function statusLabel(status: LongWallsMilestoneStatus): string {
  if (status === "validated") return "Validated";
  if (status === "running") return "Running";
  if (status === "blocked") return "Blocked";
  return "Planned";
}

function statusTone(status: LongWallsMilestoneStatus): string {
  if (status === "validated") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "running") return "border-blue-200 bg-blue-50 text-blue-700";
  if (status === "blocked") return "border-red-200 bg-red-50 text-red-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function versionTone(version: LongWallsProductVersion): string {
  if (version === "v3") return "from-slate-950 to-slate-700 text-white";
  if (version === "v2") return "from-cyan-700 to-teal-600 text-white";
  if (version === "v1") return "from-blue-700 to-indigo-600 text-white";
  return "from-slate-200 to-white text-slate-800";
}

function shortVersionLabel(version: LongWallsProductVersion): string {
  if (version === "foundation") return "Foundation";
  return version.toUpperCase();
}

function LongTermMilestoneCard({
  milestone,
  index,
}: {
  milestone: LongWallsStrategicMilestone;
  index: number;
}): JSX.Element {
  return (
    <article
      className="relative overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-sm"
      data-testid={`longwalls-milestone-${milestone.id}`}
    >
      <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-600 via-cyan-500 to-emerald-400" />
      <div className="space-y-5 p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(milestone.status)}`}>
                {statusLabel(milestone.status)}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                {milestone.horizon}
              </span>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500">
                Strategic milestone #{index + 1}
              </span>
            </div>
            <h3 className="mt-3 max-w-4xl text-2xl font-black tracking-tight text-slate-950">{milestone.title}</h3>
            <p className="mt-3 max-w-5xl text-sm leading-6 text-slate-600">{milestone.headline}</p>
          </div>
          <div
            className={`shrink-0 rounded-2xl bg-gradient-to-br px-4 py-3 text-center shadow-sm ${versionTone(
              milestone.targetVersion,
            )}`}
          >
            <div className="text-xs font-semibold uppercase tracking-[0.24em] opacity-80">Product horizon</div>
            <div className="text-2xl font-black">{shortVersionLabel(milestone.targetVersion)}</div>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Why it matters</div>
            <p className="mt-2 text-sm leading-6 text-slate-700">{milestone.strategicMeaning}</p>
          </div>
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-700">Proof summary</div>
            <p className="mt-2 text-sm leading-6 text-blue-950">{milestone.proofSummary}</p>
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              What was proven
            </div>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-emerald-950">
              {milestone.proofHighlights.map((item) => (
                <li key={`${milestone.id}-proof-${item}`}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">What remains</div>
            <ul className="mt-3 space-y-2 text-sm leading-6 text-amber-950">
              {milestone.remaining.map((item) => (
                <li key={`${milestone.id}-remaining-${item}`}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Concrete tests or gates</div>
          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            {milestone.tests.map((test) => (
              <div key={`${milestone.id}-${test.name}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-blue-700">{test.scope}</div>
                <div className="mt-1 text-sm font-semibold text-slate-950">{test.name}</div>
                {test.command ? (
                  <code className="mt-2 block overflow-hidden text-ellipsis rounded-lg bg-slate-950 px-3 py-2 text-xs text-slate-100">
                    {test.command}
                  </code>
                ) : null}
                <p className="mt-2 text-xs leading-5 text-slate-600">{test.proof}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-3 lg:grid-cols-[1fr_0.8fr]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Evidence refs</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {milestone.evidenceRefs.map((ref) => (
                <span
                  key={`${milestone.id}-${ref}`}
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-600"
                >
                  {ref}
                </span>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-white p-4 text-xs leading-5 text-amber-900">
            <strong>Claim boundary:</strong> {milestone.claimBoundary}
          </div>
        </div>
      </div>
    </article>
  );
}

export function LongWallsMilestonesScreen(): JSX.Element {
  const grouped = groupMilestonesByVersion();
  const summary = summarizeMilestones();
  const remainingMilestones = longwallsMilestones.filter((milestone) => milestone.status !== "validated");

  return (
    <div className="mx-auto max-w-7xl space-y-8 px-4 py-8 sm:px-6 lg:px-8" data-testid="longwalls-milestones-screen">
      <section className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-xl sm:p-8">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-cyan-500 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-blue-600 blur-3xl" />
        </div>
        <div className="relative grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.35em] text-cyan-200">
              LongWalls strategic milestones
            </div>
            <h1 className="mt-4 max-w-4xl text-4xl font-black tracking-tight sm:text-5xl">
              From November 2025 to the V3 frontier.
            </h1>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-200 sm:text-base">
              This is the long-view version of the roadmap: fewer milestones, stronger strategic meaning, and explicit
              boundaries between what is proven, what is running, and what remains before listed-company-grade autonomy.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-300">Strategic milestones</div>
              <div className="mt-1 text-3xl font-black">{summary.total}</div>
            </div>
            <div className="rounded-2xl border border-emerald-300/20 bg-emerald-400/10 p-4">
              <div className="text-xs uppercase tracking-wide text-emerald-100">Validated</div>
              <div className="mt-1 text-3xl font-black">{summary.validated}</div>
            </div>
            <div className="rounded-2xl border border-blue-300/20 bg-blue-400/10 p-4">
              <div className="text-xs uppercase tracking-wide text-blue-100">Running</div>
              <div className="mt-1 text-3xl font-black">{summary.running}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
              <div className="text-xs uppercase tracking-wide text-slate-300">Still ahead</div>
              <div className="mt-1 text-3xl font-black">{summary.planned + summary.blocked}</div>
            </div>
          </div>
        </div>
      </section>

      <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">
            Canonical product horizons
          </div>
          <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
            These are strategic product versions, not script names. A technical `v1` gate can contribute to a product
            horizon without closing it.
          </p>
        </div>
        <div className="grid gap-3 lg:grid-cols-4">
          {longwallsVersionDefinitions.map((definition) => (
            <div key={definition.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-sm font-black text-slate-950">{definition.label}</div>
              <p className="mt-2 text-xs leading-5 text-slate-600">{definition.strategicMeaning}</p>
              <p className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-700">
                <strong>Closure rule:</strong> {definition.closureRule}
              </p>
            </div>
          ))}
        </div>
      </Card>

      <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Where we are now</div>
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-sm font-semibold text-emerald-900">A bounded V1 is credible</div>
            <p className="mt-1 text-xs leading-5 text-emerald-800">
              Atlas native, Foundry strict contract, Jarvis local loop, evidence ledger and runtime governance exist in
              bounded supported scope.
            </p>
          </div>
          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <div className="text-sm font-semibold text-blue-900">A75 is the live blocker</div>
            <p className="mt-1 text-xs leading-5 text-blue-800">
              The 48h, 12-lane run must finalize before Test 2 Codex can unlock. Running is not validated.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-sm font-semibold text-amber-900">V2/V3 remain ahead</div>
            <p className="mt-1 text-xs leading-5 text-amber-800">
              Commercial execution nodes, deep Codex lanes, live controls and multi-app scale are future proof gates,
              not current claims.
            </p>
          </div>
        </div>
      </Card>

      <section className="space-y-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">Strategic timeline</div>
          <h2 className="mt-1 text-3xl font-black tracking-tight text-slate-950">
            The few milestones that explain the journey.
          </h2>
        </div>
        <div className="space-y-5">
          {longwallsMilestones.map((milestone, index) => (
            <LongTermMilestoneCard key={milestone.id} milestone={milestone} index={index} />
          ))}
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        {versionOrder.map((version) => (
          <div key={version} className={`rounded-3xl bg-gradient-to-br p-5 shadow-sm ${versionTone(version)}`}>
            <div className="text-xs font-semibold uppercase tracking-[0.24em] opacity-80">
              {shortVersionLabel(version)}
            </div>
            <div className="mt-2 text-3xl font-black">{grouped[version].length}</div>
            <div className="mt-1 text-sm font-semibold opacity-90">strategic milestones</div>
          </div>
        ))}
      </section>

      <Card className="border-slate-200 bg-white shadow-sm" bodyClassName="space-y-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500">What remains from here</div>
          <h2 className="mt-1 text-2xl font-black text-slate-950">The next gates that decide whether the vision is real.</h2>
        </div>
        <div className="grid gap-3 lg:grid-cols-3">
          {remainingMilestones.map((milestone) => (
            <div key={`${milestone.id}-remaining-summary`} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(milestone.status)}`}>
                  {statusLabel(milestone.status)}
                </span>
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500">
                  {shortVersionLabel(milestone.targetVersion)}
                </span>
              </div>
              <div className="mt-3 text-sm font-black text-slate-950">{milestone.title}</div>
              <p className="mt-2 text-xs leading-5 text-slate-600">{milestone.remaining.join(" ")}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
